import React, { useState, useEffect } from 'react';
import { sessionsApi } from '../../services/api/sessions.api';
import { sessionProgramsApi } from '../../services/api/session-programs.api';
import { facilitatorsApi } from '../../services/api/facilitators.api';
import { authApi, type AdminAuthUser } from '../../services/api/auth.api';
import { canPerformAdminAction } from '../../utils/adminPermissions';
import type { Facilitator, GameSession, Language, SessionProgram } from '@business-game/shared';
import { SuperAdminOverview } from './SuperAdminOverview';
import { SessionFormPanel } from '../../components/admin/SessionFormPanel';
import { SessionSharePanel } from '../../components/admin/SessionSharePanel';
import { SessionModal, SessionModalFooter } from '../../components/admin/SessionModal';

export const Admin: React.FC = () => {
  const [sessions, setSessions] = useState<GameSession[]>([]);
  const [programs, setPrograms] = useState<SessionProgram[]>([]);
  const [facilitators, setFacilitators] = useState<Facilitator[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentAdminUser, setCurrentAdminUser] = useState<AdminAuthUser | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalTab, setEditModalTab] = useState<'edit' | 'share'>('edit');
  const [editingSession, setEditingSession] = useState<GameSession | null>(null);
  const [archiveTargetSessions, setArchiveTargetSessions] = useState<GameSession[] | null>(null);
  const [isArchivingSession, setIsArchivingSession] = useState(false);
  const [deleteTargetSessions, setDeleteTargetSessions] = useState<GameSession[] | null>(null);
  const [isDeletingSession, setIsDeletingSession] = useState(false);
  const [newSession, setNewSession] = useState({
    name: '',
    gameMode: 'teamlab' as 'teamlab' | 'leadlab',
    language: 'fr' as Language,
    timezone: 'Europe/Paris',
    startDate: '',
    isTest: false,
    notes: '',
    programId: '' as number | '',
    programSlotId: '' as number | '',
  });
  const [editSession, setEditSession] = useState({
    name: '',
    gameMode: 'teamlab' as 'teamlab' | 'leadlab',
    language: 'fr' as Language,
    timezone: 'Europe/Paris',
    startDate: '',
    isTest: false,
    notes: '',
    programId: '' as number | '',
    programSlotId: '' as number | '',
  });
  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        const user = await authApi.me();
        setCurrentAdminUser(user);
        await loadSessions(true);
        await loadPrograms();
        await loadFacilitators();
      } catch (error) {
        console.error('Failed to initialize admin page:', error);
        setLoading(false);
      }
    };

    void initialize();
  }, []);

  const loadPrograms = async () => {
    try {
      const data = await sessionProgramsApi.getAllPrograms(true);
      setPrograms(data);
    } catch (error) {
      console.error('Failed to load programs:', error);
    }
  };

  const loadFacilitators = async () => {
    try {
      const data = await facilitatorsApi.getAllFacilitators();
      setFacilitators(data);
    } catch (error) {
      console.error('Failed to load facilitators:', error);
    }
  };

  const loadSessions = async (includeArchived = true, options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const data = await sessionsApi.getAllSessions(false, includeArchived);
      setSessions(data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Restores the window scroll position after React has re-rendered the sessions list.
  // Restoring on two consecutive frames also covers late layout shifts (charts, images)
  // that happen right after the refreshed list is painted.
  const restoreScrollAfterRender = (scrollY: number) => {
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
    });
  };

  // Session mutations (create/update/archive/unarchive/delete) optimistically update the
  // list, then silently reload it from the API. Both steps replace the sessions array and
  // re-render <SuperAdminOverview>, which can push the page back to the top. Capture the
  // scroll position before the re-renders and restore it after each one.
  const refreshSessionsPreservingScroll = () => {
    const scrollY = window.scrollY;
    restoreScrollAfterRender(scrollY);
    void loadSessions(true, { silent: true }).finally(() => restoreScrollAfterRender(scrollY));
  };

  const openCreateModal = () => {
    setNewSession({
      name: '',
      gameMode: 'teamlab',
      language: 'fr',
      timezone: 'Europe/Paris',
      startDate: '',
      isTest: false,
      notes: '',
      programId: '',
      programSlotId: '',
    });
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewSession({
      name: '',
      gameMode: 'teamlab',
      language: 'fr',
      timezone: 'Europe/Paris',
      startDate: '',
      isTest: false,
      notes: '',
      programId: '',
      programSlotId: '',
    });
  };

  const selectedProgram = programs.find((program) => program.id === newSession.programId);

  const getProgramSlotLabel = (session: GameSession) => {
    if (!session.programId || !session.programSlotId) return undefined;
    const program = programs.find((entry) => entry.id === session.programId);
    if (!program) return undefined;
    const slotIndex = program.slots.findIndex((slot) => slot.id === session.programSlotId);
    if (slotIndex < 0) return undefined;
    const slot = program.slots[slotIndex];
    return slot.label || `Séance ${slotIndex + 1}`;
  };

  const applyProgramDefaults = (programId: number | '') => {
    if (!programId) {
      setNewSession((prev) => ({
        ...prev,
        programId: '',
        programSlotId: '',
      }));
      return;
    }

    const program = programs.find((entry) => entry.id === programId);
    if (!program) return;

    setNewSession((prev) => ({
      ...prev,
      programId,
      programSlotId: '',
      gameMode: program.gameMode,
      startDate: '',
    }));
  };

  const applyProgramSlot = (slotId: number | '') => {
    if (!slotId || !selectedProgram) {
      setNewSession((prev) => ({ ...prev, programSlotId: '', startDate: '' }));
      return;
    }

    const slot = selectedProgram.slots.find((entry) => entry.id === slotId);
    if (!slot) return;

    setNewSession((prev) => ({
      ...prev,
      programSlotId: slotId,
      startDate: new Date(slot.scheduledAt).toISOString().slice(0, 16),
      language: slot.language || prev.language,
      timezone: slot.timezone || prev.timezone,
    }));
  };

  const handleCreateSession = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const created = await sessionsApi.createSession({
        name: newSession.name,
        gameMode: newSession.gameMode,
        language: newSession.language,
        timezone: newSession.timezone,
        startDate: newSession.startDate ? new Date(newSession.startDate) : undefined,
        isTest: newSession.isTest,
        notes: newSession.notes || undefined,
        programId: newSession.programId || undefined,
        programSlotId: newSession.programSlotId || undefined,
      });
      setSessions((prev) => [created, ...prev.filter((session) => session.id !== created.id)]);
      closeCreateModal();
      refreshSessionsPreservingScroll();
    } catch (error) {
      console.error('Failed to create session:', error);
      alert((error as any)?.response?.data?.error?.message || 'Erreur lors de la création de la session');
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditModalTab('edit');
    setEditingSession(null);
  };

  const handleToggleTrainerEmail = async (session: GameSession, sent: boolean) => {
    const previous = session.trainerEmailSentAt ?? null;
    const patch = (value: Date | null) =>
      setSessions((prev) => prev.map((item) => (item.id === session.id ? { ...item, trainerEmailSentAt: value } : item)));
    patch(sent ? new Date() : null);
    try {
      const updated = await sessionsApi.updateSession(session.id, { trainerEmailSent: sent });
      patch(updated.trainerEmailSentAt ?? null);
    } catch (error) {
      console.error('Failed to update trainer email status:', error);
      patch(previous);
    }
  };

  const handleEditSession = (session: GameSession) => {
    setEditingSession(session);
    setEditModalTab('edit');
    setEditSession({
      name: session.name || '',
      gameMode: session.gameMode,
      language: session.language || 'fr',
      timezone: session.timezone || 'Europe/Paris',
      startDate: session.startDate ? new Date(session.startDate).toISOString().slice(0, 16) : '',
      isTest: session.isTest,
      notes: session.notes || '',
      programId: session.programId ?? '',
      programSlotId: session.programSlotId ?? '',
    });
    setShowEditModal(true);
  };

  const handleUpdateSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSession) return;
    try {
      await sessionsApi.updateSession(editingSession.id, {
        name: editSession.name,
        gameMode: editSession.gameMode,
        language: editSession.language,
        timezone: editSession.timezone,
        startDate: editSession.startDate ? new Date(editSession.startDate) : undefined,
        isTest: editSession.isTest,
        notes: editSession.notes || undefined,
      });
      setShowEditModal(false);
      setEditModalTab('edit');
      setEditingSession(null);
      refreshSessionsPreservingScroll();
    } catch (error) {
      console.error('Failed to update session:', error);
      alert((error as any)?.response?.data?.error?.message || 'Erreur lors de la modification de la session');
    }
  };

  const requestArchiveSession = (session: GameSession) => {
    setArchiveTargetSessions([session]);
  };

  const handleArchiveFromEditModal = () => {
    if (!editingSession) return;
    closeEditModal();
    requestArchiveSession(editingSession);
  };

  const requestArchiveSessions = (sessions: GameSession[]) => {
    if (sessions.length === 0) return;
    setArchiveTargetSessions(sessions);
  };

  const handleArchiveSessions = async () => {
    if (!archiveTargetSessions?.length) {
      return;
    }

    const targetIds = new Set(archiveTargetSessions.map((session) => session.id));

    try {
      setIsArchivingSession(true);
      for (const session of archiveTargetSessions) {
        await sessionsApi.archiveSession(session.id);
      }
      setSessions((prev) =>
        prev.map((session) =>
          targetIds.has(session.id)
            ? {
                ...session,
                archivedAt: new Date(),
                archivedReason: 'archived_from_admin',
                isClosed: true,
              }
            : session
        )
      );
      setArchiveTargetSessions(null);
      refreshSessionsPreservingScroll();
    } catch (error) {
      console.error('Failed to archive session(s):', error);
      alert('Erreur lors de l’archivage');
    } finally {
      setIsArchivingSession(false);
    }
  };

  const handleUnarchiveSession = async (session: GameSession) => {
    try {
      await sessionsApi.unarchiveSession(session.id);
      setSessions((prev) =>
        prev.map((item) =>
          item.id === session.id
            ? { ...item, archivedAt: null, archivedReason: null, isClosed: false }
            : item
        )
      );
      refreshSessionsPreservingScroll();
    } catch (error) {
      console.error('Failed to unarchive session:', error);
      alert('Erreur lors du désarchivage');
    }
  };

  const requestDeleteSession = (session: GameSession) => {
    setDeleteTargetSessions([session]);
  };

  const requestDeleteSessions = (sessions: GameSession[]) => {
    if (sessions.length === 0) return;
    setDeleteTargetSessions(sessions);
  };

  const handleDeleteSessions = async () => {
    if (!deleteTargetSessions?.length) return;

    const targetIds = new Set(deleteTargetSessions.map((session) => session.id));

    try {
      setIsDeletingSession(true);
      for (const session of deleteTargetSessions) {
        await sessionsApi.deleteSession(session.id);
      }
      setSessions((prev) => prev.filter((session) => !targetIds.has(session.id)));
      setDeleteTargetSessions(null);
      refreshSessionsPreservingScroll();
    } catch (error) {
      console.error('Failed to delete session(s):', error);
      alert((error as any)?.response?.data?.error?.message || 'Erreur lors de la suppression');
    } finally {
      setIsDeletingSession(false);
    }
  };

  return (
    <div className="flex min-h-full flex-col font-sans text-slate-900">
      {/* Top toolbar - aligned with sidebar */}
      <div className="sticky top-0 z-10 -mr-6 -mt-6 mb-6 flex items-center justify-between border-b border-indigo-200/60 bg-white/80 px-6 py-4 shadow-sm shadow-indigo-500/5 backdrop-blur-sm lg:-mr-8 lg:-mt-8 lg:mb-8 lg:px-8">
        <h2 className="text-lg font-bold text-slate-900">
          Sessions
          {refreshing ? <span className="ml-2 text-xs font-medium text-slate-400">Mise à jour…</span> : null}
        </h2>

        <div className="flex items-center gap-4">
          {canPerformAdminAction(currentAdminUser, 'sessionsCreate') && (
            <button
              onClick={openCreateModal}
              className="hidden sm:flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-2xl shadow-lg shadow-indigo-200 transition-transform transform hover:-translate-y-0.5 active:scale-95 font-bold text-sm"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              NOUVELLE SESSION
            </button>
          )}

          <div className="h-8 w-px bg-slate-200/60 hidden sm:block" />

          <button
            onClick={() => {
              localStorage.removeItem('teamlab_admin_token');
              window.location.href = '/admin/login';
            }}
            className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/50 hover:bg-red-50 text-slate-400 hover:text-red-500 border border-slate-200 hover:border-red-200 transition-all shadow-sm"
            title="Se déconnecter"
          >
            <svg
              className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow w-full px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-slate-500">Chargement...</p>
          </div>
        ) : (
          <SuperAdminOverview
            sessions={sessions}
            programs={programs}
            facilitators={facilitators}
            showSuperAdminInsights={!!currentAdminUser?.canManageAllUsers}
            onEditSession={
              canPerformAdminAction(currentAdminUser, 'sessionsEdit') ? handleEditSession : undefined
            }
            onArchiveSession={
              canPerformAdminAction(currentAdminUser, 'sessionsArchive') ? requestArchiveSession : undefined
            }
            onArchiveSessions={
              canPerformAdminAction(currentAdminUser, 'sessionsArchive') ? requestArchiveSessions : undefined
            }
            onUnarchiveSession={
              canPerformAdminAction(currentAdminUser, 'sessionsArchive') ? handleUnarchiveSession : undefined
            }
            onDeleteSession={
              canPerformAdminAction(currentAdminUser, 'sessionsDelete') ? requestDeleteSession : undefined
            }
            onDeleteSessions={
              canPerformAdminAction(currentAdminUser, 'sessionsDelete') ? requestDeleteSessions : undefined
            }
            onToggleTrainerEmail={
              canPerformAdminAction(currentAdminUser, 'sessionsEdit') ? handleToggleTrainerEmail : undefined
            }
          />
        )}
      </main>

      {showCreateModal && (
        <SessionModal
          mode="create"
          onClose={closeCreateModal}
          footer={
            <SessionModalFooter
              mode="create"
              onCancel={closeCreateModal}
              formId="create-session-form"
              submitDisabled={!newSession.name.trim()}
            />
          }
        >
          <form id="create-session-form" onSubmit={handleCreateSession}>
            <SessionFormPanel
              mode="create"
              values={newSession}
              onChange={(patch) => setNewSession((prev) => ({ ...prev, ...patch }))}
              programs={programs}
              selectedProgram={selectedProgram}
              onApplyProgramDefaults={applyProgramDefaults}
              onApplyProgramSlot={applyProgramSlot}
            />
          </form>
        </SessionModal>
      )}

      {showEditModal && editingSession && (
        <SessionModal
          mode="edit"
          onClose={closeEditModal}
          session={editingSession}
          programSlotLabel={getProgramSlotLabel(editingSession)}
          tab={editModalTab}
          onTabChange={setEditModalTab}
          footer={
            editModalTab === 'edit' ? (
              <SessionModalFooter
                mode="edit"
                onCancel={closeEditModal}
                formId="edit-session-form"
                canArchive={canPerformAdminAction(currentAdminUser, 'sessionsArchive') && !editingSession.archivedAt}
                onArchive={handleArchiveFromEditModal}
              />
            ) : (
              <SessionModalFooter mode="edit" onCancel={closeEditModal} />
            )
          }
        >
          {editModalTab === 'edit' ? (
            <form id="edit-session-form" onSubmit={handleUpdateSession}>
              <SessionFormPanel
                mode="edit"
                values={editSession}
                onChange={(patch) => setEditSession((prev) => ({ ...prev, ...patch }))}
              />
            </form>
          ) : (
            <SessionSharePanel session={editingSession} />
          )}
        </SessionModal>
      )}

      {deleteTargetSessions && deleteTargetSessions.length > 0 && (
        <div className="fixed inset-0 z-[100]" aria-labelledby="delete-session-title" role="dialog" aria-modal="true">
          <div
            className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity"
            onClick={() => {
              if (!isDeletingSession) setDeleteTargetSessions(null);
            }}
          ></div>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative w-full max-w-xl transform overflow-hidden rounded-3xl bg-white text-left shadow-2xl ring-1 ring-slate-200 transition-all">
                <button
                  type="button"
                  onClick={() => {
                    if (!isDeletingSession) setDeleteTargetSessions(null);
                  }}
                  className="absolute top-6 right-6 z-20 rounded-xl bg-slate-50 p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="p-8">
                  <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-700 shadow-sm">
                    <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>

                  <div className="text-xs font-bold uppercase tracking-[0.25em] text-red-600">Suppression définitive</div>
                  <h3 id="delete-session-title" className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                    {deleteTargetSessions.length === 1
                      ? `Supprimer la session ${deleteTargetSessions[0].code}`
                      : `Supprimer ${deleteTargetSessions.length} sessions`}
                  </h3>
                  {deleteTargetSessions.length === 1 ? (
                    <p className="mt-3 text-sm leading-6 text-slate-600">
                      La session{' '}
                      <span className="font-bold text-slate-900">
                        {deleteTargetSessions[0].name || deleteTargetSessions[0].code}
                      </span>{' '}
                      sera
                      <span className="font-bold text-red-600"> supprimée définitivement</span>. Toutes les données associées
                      (décisions, statistiques) seront perdues.
                    </p>
                  ) : (
                    <div className="mt-3 space-y-3">
                      <p className="text-sm leading-6 text-slate-600">
                        Les <span className="font-bold text-slate-900">{deleteTargetSessions.length} sessions</span> sélectionnées
                        seront <span className="font-bold text-red-600">supprimées définitivement</span>. Toutes les données associées
                        seront perdues.
                      </p>
                      <div className="max-h-40 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left text-sm text-slate-700">
                        <ul className="space-y-1">
                          {deleteTargetSessions.map((session) => (
                            <li key={session.id}>
                              <span className="font-bold text-slate-900">{session.code}</span>
                              {session.name ? ` · ${session.name}` : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    Cette action est irréversible. En cas de doute, préférez l’archivage.
                  </div>

                  <div className="mt-8 flex gap-3">
                    <button
                      type="button"
                      onClick={() => setDeleteTargetSessions(null)}
                      disabled={isDeletingSession}
                      className="flex-1 rounded-2xl bg-slate-100 px-5 py-3 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDeleteSessions()}
                      disabled={isDeletingSession}
                      className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-red-200 transition-all hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isDeletingSession
                        ? 'Suppression...'
                        : deleteTargetSessions.length === 1
                          ? 'Confirmer la suppression'
                          : `Supprimer ${deleteTargetSessions.length} sessions`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {archiveTargetSessions && archiveTargetSessions.length > 0 && (
        <div className="fixed inset-0 z-[100]" aria-labelledby="archive-session-title" role="dialog" aria-modal="true">
          <div
            className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity"
            onClick={() => {
              if (!isArchivingSession) {
                setArchiveTargetSessions(null);
              }
            }}
          ></div>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative w-full max-w-xl transform overflow-hidden rounded-3xl bg-white text-left shadow-2xl ring-1 ring-slate-200 transition-all">
                <button
                  type="button"
                  onClick={() => {
                    if (!isArchivingSession) {
                      setArchiveTargetSessions(null);
                    }
                  }}
                  className="absolute top-6 right-6 z-20 rounded-xl bg-slate-50 p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="p-8">
                  <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-700 shadow-sm">
                    <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7h16m-1 0v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7m3-3h8l1 3H7l1-3z" />
                    </svg>
                  </div>

                  <div className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600">Archivage</div>
                  <h3 id="archive-session-title" className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                    {archiveTargetSessions.length === 1
                      ? `Archiver la session ${archiveTargetSessions[0].code}`
                      : `Archiver ${archiveTargetSessions.length} sessions`}
                  </h3>
                  {archiveTargetSessions.length === 1 ? (
                    <p className="mt-3 text-sm leading-6 text-slate-600">
                      La session{' '}
                      <span className="font-bold text-slate-900">
                        {archiveTargetSessions[0].name || archiveTargetSessions[0].code}
                      </span>{' '}
                      sera fermée, retirée des listes actives et conservée dans les sessions archivées.
                    </p>
                  ) : (
                    <div className="mt-3 space-y-3">
                      <p className="text-sm leading-6 text-slate-600">
                        Les <span className="font-bold text-slate-900">{archiveTargetSessions.length} sessions</span> sélectionnées
                        seront fermées, retirées des listes actives et conservées dans les sessions archivées.
                      </p>
                      <div className="max-h-40 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left text-sm text-slate-700">
                        <ul className="space-y-1">
                          {archiveTargetSessions.map((session) => (
                            <li key={session.id}>
                              <span className="font-bold text-slate-900">{session.code}</span>
                              {session.name ? ` · ${session.name}` : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    Elles ne seront pas supprimées de la base. Tu pourras les retrouver dans les sessions archivées.
                  </div>

                  <div className="mt-8 flex gap-3">
                    <button
                      type="button"
                      onClick={() => setArchiveTargetSessions(null)}
                      disabled={isArchivingSession}
                      className="flex-1 rounded-2xl bg-slate-100 px-5 py-3 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleArchiveSessions()}
                      disabled={isArchivingSession}
                      className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-amber-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-amber-200 transition-all hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isArchivingSession
                        ? 'Archivage...'
                        : archiveTargetSessions.length === 1
                          ? 'Confirmer l’archivage'
                          : `Archiver ${archiveTargetSessions.length} sessions`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
